import { Grid, Typography, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import { useFormikContext } from 'formik';
import LogRocket from 'logrocket';
import { type FC, type MutableRefObject, useContext, useState } from 'react';

import { SlabContext } from '@core/context/SlabContext';
import type { ProjectFormikType } from '@core/formik/project';
import Enums from '@core/types/api/Enums';
import type { Product } from '@core/types/api/Product/Product';
import { hasValue } from '@core/utils/equality';
import { buildProjectConfigFormikValues } from '@core/utils/projectConfigHelpers';
import { requiredLabel } from '@core/utils/strings';
import { useToast } from '@web/providers/ToastProvider/useToast';

import { ApiLookupInput } from '../../../../components/LookupInput/ApiLookupInput';
import type { LookupInputOption } from '../../../../components/LookupInput/LookupInputSharedComponents';
import { useSlabQueryFetcher } from '../../../../hooks/useSlabQuery';
import { useTenantMargins } from '../../../../hooks/useTenantMargins';
import type { QueryRouteBarrelTypes } from '../../../../utils/ApiClient';
import { lookups } from '../../../../utils/DomainHelpers';

type Props = {
  curProductsRef: MutableRefObject<Record<string, Product>>;
};

export const ProjectConfigSection: FC<Props> = ({ curProductsRef }) => {
  const ctx = useContext(SlabContext);
  const usesDispatchCustomer = ctx.userInfo.hasFlags([
    Enums.FeatureFlagName.FeatureFlagDispatchCustomer,
  ]);
  const isConfigRequired = ctx.userInfo.hasFlags([
    Enums.FeatureFlagName.FeatureFlagRequiresProjectConfig,
  ]);
  const isMultiLocationEnabled = ctx.userInfo.hasFlags([
    Enums.FeatureFlagName.FeatureFlagMultiLocationEnabled,
  ]);
  const defaultToSuggestedPrice = ctx.userInfo.hasFlags([
    Enums.FeatureFlagName.FeatureFlagDefaultPriceToSuggestedPrice,
  ]);
  const theme = useTheme();
  const formikBag = useFormikContext<ProjectFormikType>();
  const toast = useToast();
  const [isApplyingConfig, setIsApplyingConfig] = useState(false);

  const { defaultIsMoneySwitchOn, marginConfiguration, defaultMargins } = useTenantMargins();

  const fetchProjectConfig = useSlabQueryFetcher<'GET project config by ID'>();
  const fetchProjectConfigProducts =
    useSlabQueryFetcher<'GET project config products by project config ID'>();

  // Applied from the handler, not an effect: the section unmounts when the user switches
  // steps, and Formik's setters keep working on the drawer's form after that.
  const applyProjectConfig = async (id: string): Promise<void> => {
    setIsApplyingConfig(true);
    try {
      const [projectConfig, projectConfigProducts] = await Promise.all([
        fetchProjectConfig('GET project config by ID', { pathParams: { id } }),
        fetchProjectConfigProducts('GET project config products by project config ID', {
          pathParams: { id },
        }),
      ]);

      // The form may have been reset or closed while the config was loading.
      await formikBag.setValues((prev) =>
        prev.projectConfig?.id === id
          ? {
              ...prev,
              ...buildProjectConfigFormikValues({
                projectConfig,
                projectConfigProducts,
                currentProductsByPlants: prev.productsByPlants,
                shouldMergeProducts: isMultiLocationEnabled,
                usesDispatchCustomer,
                defaultToSuggestedPrice,
                defaultIsMoneySwitchOn,
                defaultMargins,
                marginConfiguration,
              }),
            }
          : prev,
      );

      curProductsRef.current = projectConfigProducts.reduce(
        (acc, p) => ({ ...acc, [p.product.id]: p.product }),
        curProductsRef.current,
      );
    } catch (error) {
      LogRocket.error('Error fetching project config or products', error);
      toast.error({
        message: 'Could not load the configuration template. Please select it again.',
      });
      await formikBag.setValues((prev) =>
        prev.projectConfig?.id === id
          ? { ...prev, projectConfig: { id: null, option: null } }
          : prev,
      );
    } finally {
      setIsApplyingConfig(false);
    }
  };

  return (
    <Box display='flex' flexDirection='column' gap='1rem'>
      <Box paddingBottom='0.5rem'>
        <Typography variant='h6' color={theme.palette.warning.main}>
          Warning: Selecting a new project config will update contractors, other companies,
          products, and override details.
        </Typography>
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <ApiLookupInput
            formState={formikBag.values}
            name='projectConfig'
            label={requiredLabel('Configuration template', isConfigRequired)}
            route={{
              barrel: 'GET project config summaries',
              args: (
                inputText,
                { constructQueryParams },
              ): QueryRouteBarrelTypes['GET project config summaries']['args'] =>
                constructQueryParams(inputText, {
                  filterBy: [
                    {
                      name: 'isArchived',
                      operation: Enums.FilterOperation.Equals,
                      values: [false],
                    },
                    {
                      name: 'plantIsArchived',
                      operation: Enums.FilterOperation.Equals,
                      values: [false],
                    },
                  ],
                }),
              transform: (projectConfigSummaryList): LookupInputOption[] =>
                lookups({
                  ...projectConfigSummaryList,
                  label: (t) => t.name,
                }),
            }}
            disabled={isApplyingConfig}
            onMatchChange={(id): void => {
              if (hasValue(id)) {
                applyProjectConfig(id);
              }
            }}
          />
        </Grid>
      </Grid>
    </Box>
  );
};
